/**
 * How long ago something was true, said the way a person says it.
 *
 * One implementation, because an age is the load-bearing half of every
 * freshness claim on this product and three components rounding it three ways
 * is how "2 мин" and "минуту назад" end up beside each other describing the
 * same instant.
 *
 * Deliberately coarse above a minute. A reader deciding whether to trust a
 * screen does not need seconds, and "7 мин" is read faster than "7 мин 12 с".
 */

export function humanAge(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  if (whole < 60) return `${String(whole)} с`;

  const minutes = Math.floor(whole / 60);
  if (minutes < 60) return `${String(minutes)} мин`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest === 0 ? `${String(hours)} ч` : `${String(hours)} ч ${String(rest)} мин`;
  }

  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest === 0 ? `${String(days)} д` : `${String(days)} д ${String(rest)} ч`;
}

/** The clock time an instant reads as, in the reader's own zone. */
const CLOCK = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

export function clockOf(instant: string): string {
  const moment = new Date(instant);
  // An unreadable timestamp is shown as it arrived rather than as "Invalid
  // Date": the raw text at least says something true about what came back.
  return Number.isNaN(moment.getTime()) ? instant : CLOCK.format(moment);
}
